import {
  type AwgSize,
  type CalcResult,
  err,
  isAwgSize,
  isNonNegativeInteger,
  ok,
} from "../types.ts";
import {
  type InsulationType,
  TABLE_5_AREA_IN2,
  conductorAreaIn2,
} from "../tables/conductors.ts";
import {
  type ConduitType,
  type TradeSize,
  allowableFillFraction,
  conduitTotalAreaIn2,
  TABLE_4_TOTAL_AREA_IN2,
} from "../tables/conduit.ts";

export type ConduitFillConductor = {
  awg: AwgSize;
  insulation: InsulationType;
  count: number;
};

export type ConduitFillInput = {
  conduitType: ConduitType;
  tradeSize: TradeSize;
  conductors: ConduitFillConductor[];
};

export type ConduitFillResult = {
  conductorCount: number;
  totalConductorAreaIn2: number;
  conduitAreaIn2: number;
  allowableFillPercent: number; // the code limit (53/31/40)
  actualFillPercent: number; // how full it actually is
  allowableAreaIn2: number; // conduitArea × allowable fraction
  overfilled: boolean;
};

/**
 * Conduit fill per NEC Chapter 9, Table 1 (allowable %) using conductor areas
 * from Table 5 and conduit interior areas from Table 4.
 *
 *   allowable % = 53 (1 conductor) / 31 (2) / 40 (3 or more)
 *   actual %    = Σ(conductor area) / conduit interior area × 100
 *   over-fill   = actual % exceeds allowable %
 */
export function conduitFill(input: ConduitFillInput): CalcResult<ConduitFillResult> {
  const { conduitType, tradeSize, conductors } = input;

  if (!(conduitType in TABLE_4_TOTAL_AREA_IN2)) {
    return err(`Unsupported conduit type "${String(conduitType)}".`);
  }
  if (!(tradeSize in TABLE_4_TOTAL_AREA_IN2[conduitType])) {
    return err(`Unsupported trade size "${String(tradeSize)}" for ${conduitType}.`);
  }
  if (!Array.isArray(conductors) || conductors.length === 0) {
    return err("Add at least one conductor.");
  }

  let totalConductorAreaIn2 = 0;
  let conductorCount = 0;

  for (const c of conductors) {
    if (!isAwgSize(c.awg)) {
      return err(`Unsupported conductor size "${String(c.awg)}".`);
    }
    if (!(c.insulation in TABLE_5_AREA_IN2)) {
      return err(`Unsupported insulation type "${String(c.insulation)}".`);
    }
    if (!isNonNegativeInteger(c.count)) {
      return err("Conductor count must be a whole number ≥ 0.");
    }
    totalConductorAreaIn2 += conductorAreaIn2(c.awg, c.insulation) * c.count;
    conductorCount += c.count;
  }

  if (conductorCount === 0) {
    return err("Add at least one conductor.");
  }

  const conduitAreaIn2 = conduitTotalAreaIn2(conduitType, tradeSize);
  const fraction = allowableFillFraction(conductorCount);
  const allowableAreaIn2 = conduitAreaIn2 * fraction;
  const actualFillPercent = (totalConductorAreaIn2 / conduitAreaIn2) * 100;

  return ok({
    conductorCount,
    totalConductorAreaIn2,
    conduitAreaIn2,
    allowableFillPercent: fraction * 100,
    actualFillPercent,
    allowableAreaIn2,
    overfilled: totalConductorAreaIn2 > allowableAreaIn2,
  });
}
